import React, { useEffect, useState } from "react";
import { Radio, Store, Package, MapPin } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/time";

/**
 * Compact live-numbers strip for the home hero. Renders real counts from
 * `/api/hero-stats` — proves the platform is active on every page load
 * without needing the user to scroll.
 *
 * Fails silent: if the endpoint hiccups, the strip simply doesn't render
 * (the hero above still looks complete).
 */
export const HeroStats = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/hero-stats")
      .then((r) => !cancelled && setStats(r.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!stats) return null;

  const items = [
    {
      key: "shops",
      icon: Store,
      value: stats.dispensaries?.toLocaleString?.() || stats.dispensaries,
      label: stats.dispensaries === 1 ? "shop" : "shops",
    },
    {
      key: "products",
      icon: Package,
      value:
        stats.products >= 1000
          ? `${(stats.products / 1000).toFixed(1)}k`
          : stats.products,
      label: "products",
    },
    {
      key: "states",
      icon: MapPin,
      value: stats.states,
      label: stats.states === 1 ? "state" : "states",
    },
  ];

  return (
    <div
      data-testid="hero-stats"
      className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-foreground/80"
    >
      {items.map((it) => (
        <div
          key={it.key}
          data-testid={`hero-stat-${it.key}`}
          className="inline-flex items-baseline gap-1.5"
        >
          <it.icon className="w-3.5 h-3.5 self-center text-foreground/50" strokeWidth={1.6} />
          <span className="font-serif text-2xl sm:text-3xl tabular-nums leading-none">
            {it.value}
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">
            {it.label}
          </span>
        </div>
      ))}
      {stats.last_updated_at && (
        <div
          data-testid="hero-stat-freshness"
          className="inline-flex items-center gap-1.5 pl-4 sm:border-l sm:border-border/40"
        >
          <Radio className="w-3 h-3 text-emerald-400 animate-pulse" strokeWidth={2.4} />
          <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/85">
            Menus refreshed {timeAgo(stats.last_updated_at)}
          </span>
        </div>
      )}
    </div>
  );
};

export default HeroStats;
